import { makeAutoObservable, runInAction } from "mobx";

import { getMediaConstraints } from "../../contexts/userMediaContext";

class VideoRoomStore {
  videoRoomPlugin = null;

  displayName = "";

  janusConfig = null;

  participants = [];

  currentUserId = null;

  selfHandle = null;

  constructor(rootStore, displayName, janusConfig) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
    this.displayName = displayName;
    this.janusConfig = janusConfig;
  }

  attach() {
    if (!this.rootStore.janusInstance) return;

    this.rootStore.janusInstance.attach({
      plugin: "janus.plugin.videoroom",
      success: (pluginHandle) => {
        this.videoRoomPlugin = pluginHandle;
        console.log("[VideoRoomStore] VideoRoom plugin attached successfully");
        this.joinRoom();
      },
      error: (error) => {
        console.error("VideoRoom attach error:", error);
      },
      onmessage: (msg, jsep) => {
        console.log("msg: ", msg);
        this.handleRoomEvent(msg);
        if (jsep) {
          this.videoRoomPlugin.handleRemoteJsep({ jsep });
        }
      },
      oncleanup: () => {
        console.log("VideoRoom cleaned up");
      },
    });
  }

  joinRoom() {
    if (!this.videoRoomPlugin) return;

    this.videoRoomPlugin.send({
      message: {
        request: "join",
        room: this.janusConfig.videoRoom.room,
        ptype: "publisher",
        display: this.displayName,
      },
      success: () => {
        this.publishOwnFeed();
      },
      error: (error) => {
        console.error("[VideoRoomStore] Error joining the room:", error);
      },
    });
  }

  publishOwnFeed() {
    console.log("[VideoRoomStore] Starting to publish streams...");
    if (!this.videoRoomPlugin) {
      console.error("VideoRoom plugin is not attached.");
      return;
    }
    this.videoRoomPlugin.createOffer({
      media: {
        audioRecv: false,
        videoRecv: false,
        audioSend: true, // to join muted
        videoSend: true,
        ...getMediaConstraints(),
      },
      success: (jsep) => {
        console.log("[VideoRoomStore] Stream published successfully:", jsep);
        this.videoRoomPlugin.send({ message: { request: "configure", audio: true, video: true }, jsep });
        this.selfHandle = this.videoRoomPlugin; // Save self handle
        this.addSelfToParticipants();
        // without this delay right after joining the room, the participant list hasn't been updated yet to reflect our current state as a publisher.
        setTimeout(() => {
          this.listParticipants();
        }, 1000);
      },
      error: (error) => {
        console.error("[VideoRoomStore] Failed to publish stream:", error);
      },
    });
  }

  addSelfToParticipants() {
    runInAction(() => {
      this.participants.push({
        id: this.currentUserId,
        display: this.displayName,
        handle: this.selfHandle,
      });
    });
  }

  toggleMediaStream(participantId, type, action) {
    const id = participantId || this.currentUserId;
    const media = type === "audio" ? { audioSend: action !== "muted" } : { videoSend: action !== "muted" };
    const participant = this.participants.find((p) => p.id === id);
    if (participant && participant.handle) {
      participant.handle.createOffer({
        media,
        success: (jsep) => {
          participant.handle.send({ message: { request: "configure" }, jsep });
          console.log(`Successfully ${action}d ${type} for participant ${id}`);
        },
        error: (error) => {
          console.error(`Error when trying to ${action} ${type}:`, error);
        },
      });
    } else {
      console.error("Participant not found or does not have a valid handle:", id);
    }
  }

  handleRoomEvent(msg) {
    if (msg.videoroom === "joined") {
      console.log("msg.id", msg.id);
      runInAction(() => {
        this.currentUserId = msg.id; // Capture the current user's ID when joined
      });
      // Add all publishers as participants
      if (msg.publishers) {
        console.log("msg.publishers: ", msg.publishers);
        msg.publishers.forEach((publisher) => {
          this.addParticipant(publisher);
        });
      }
    } else if (msg.videoroom === "event") {
      if (msg.publishers) {
        msg.publishers.forEach((publisher) => {
          this.addParticipant(publisher);
        });
      }
      if (msg.leaving || msg.unpublished) {
        this.removeParticipant(msg.leaving || msg.unpublished);
      }
    }
  }

  addParticipant(publisher) {
    runInAction(() => {
      console.log("publisher: ", publisher);
      this.participants.push({
        id: publisher.id,
        display: publisher.display,
      });
    });
  }

  removeParticipant(publisherId) {
    runInAction(() => {
      this.participants = this.participants.filter((p) => p.id !== publisherId);
    });
    if (publisherId === "ok") {
      console.log("We have been unpublished or the room is cleaning up.");
    }
  }

  cleanUpRoom() {
    runInAction(() => {
      this.participants = [];
    });
  }

  listParticipants() {
    if (!this.videoRoomPlugin) return;

    console.log(this.janusConfig);

    this.videoRoomPlugin.send({
      message: {
        request: "listparticipants",
        room: this.janusConfig.videoRoom.room,
      },
      success: (result) => {
        // this.participants = result.participants;
        if (result.participants.length > 0) {
          // console.log(result.participants);
          const participant = result.participants.find(
            (roomParticipant) => roomParticipant.display === this.displayName && roomParticipant.publisher,
          );
          const publisherId = participant ? participant.id : null;
          if (publisherId) {
            this.rtpForward(publisherId);
          } else {
            console.error("[VideoRoomStore] No publisher found in the room");
          }
        }
      },
      error: (error) => {
        console.error("[VideoRoomStore] Error listing participants:", error);
      },
    });
  }

  rtpForward(publisherId) {
    if (!this.videoRoomPlugin) return;

    this.videoRoomPlugin.send({
      message: {
        request: "rtp_forward",
        room: this.janusConfig.videoRoom.room,
        secret: this.janusConfig.videoRoom.secret,
        host: this.janusConfig.videoRoom.host,
        host_family: this.janusConfig.videoRoom.host_family,
        publisher_id: publisherId,
        streams: this.janusConfig.videoRoom.streams,
        video_port: this.janusConfig.videoRoom.video_port || 8004,
      },
      success: (result) => {
        console.log("[VideoRoomStore] RTP Forwarding setup successfully:", result);
      },
      error: (error) => {
        console.error("[VideoRoomStore] Error setting up RTP Forward:", error);
      },
    });
  }

  detach() {
    if (this.videoRoomPlugin) {
      this.videoRoomPlugin.detach({
        success: () => {
          console.log("VideoRoom plugin detached successfully.");
          this.videoRoomPlugin = null;
        },
        error: (error) => {
          console.error("Failed to detach VideoRoom plugin:", error);
        },
      });
    }
  }

  leave() {
    if (this.videoRoomPlugin) {
      this.videoRoomPlugin.send({ message: { request: "unpublish" } });
      this.videoRoomPlugin.send({ message: { request: "leave" } });
      this.cleanUpRoom();
      this.detach();
    }
  }
}

export default VideoRoomStore;
